import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { groupProducts } from "@/lib/products";
import { MagnifyingGlass, Funnel } from "@phosphor-icons/react";

const CATEGORIES = ["All", "Metabolic & Weight Support", "Energy & Metabolic Support", "Sleep & Recovery", "Cognitive & Mood", "Skin, Glow & Cosmetic", "Fat Dissolvers", "Supplies"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [inStock, setInStock] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category !== "All") params.category = category;
    if (q) params.q = q;
    if (minPrice !== "") params.min_price = minPrice;
    if (maxPrice !== "") params.max_price = maxPrice;
    if (inStock) params.in_stock = true;
    const t = setTimeout(() => {
      api.get("/products", { params }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [category, q, minPrice, maxPrice, inStock]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16">
      <div className="mb-12">
        <span className="label-mono text-primary">Catalog</span>
        <h1 className="font-head text-4xl md:text-5xl mt-2">Research Peptides</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">Explore our full collection of high-purity compounds for laboratory research.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-10">
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.replace(/[^a-z]/gi, "").toLowerCase()}`}
              onClick={() => setCategory(c)}
              className={`label-mono px-4 py-2 border transition-colors ${category === c ? "border-primary text-primary bg-primary/10" : "border-black/10 text-muted-foreground hover:border-black/30"}`}
              style={{ transition: "color 0.2s, border-color 0.2s, background-color 0.2s" }}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-72">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search compounds…"
            className="w-full bg-transparent border border-black/15 pl-10 pr-4 py-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
            style={{ transition: "border-color 0.2s" }}
          />
        </div>
      </div>

      {/* Price + stock filters */}
      <div className="flex flex-wrap items-center gap-4 mb-10 border border-black/10 bg-surface px-5 py-4">
        <span className="flex items-center gap-2 label-mono text-muted-foreground"><Funnel size={15} /> Filters</span>
        <div className="flex items-center gap-2">
          <input
            data-testid="min-price-input"
            type="number" min="0" value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            placeholder="Min $"
            className="w-24 bg-transparent border border-black/15 px-3 py-2 text-sm focus:border-primary outline-none"
          />
          <span className="text-muted-foreground">–</span>
          <input
            data-testid="max-price-input"
            type="number" min="0" value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            placeholder="Max $"
            className="w-24 bg-transparent border border-black/15 px-3 py-2 text-sm focus:border-primary outline-none"
          />
        </div>
        <button
          data-testid="in-stock-toggle"
          onClick={() => setInStock((v) => !v)}
          className={`label-mono px-4 py-2 border transition-colors ${inStock ? "border-primary text-primary bg-primary/10" : "border-black/15 text-muted-foreground hover:border-black/30"}`}
          style={{ transition: "color 0.2s, border-color 0.2s, background-color 0.2s" }}
        >
          In Stock Only
        </button>
        {(minPrice !== "" || maxPrice !== "" || inStock || q) && (
          <button
            data-testid="clear-filters"
            onClick={() => { setMinPrice(""); setMaxPrice(""); setInStock(false); setQ(""); }}
            className="label-mono text-muted-foreground hover:text-destructive transition-colors ml-auto"
          >
            Clear
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground font-mono text-sm py-20 text-center">Loading catalog…</p>
      ) : products.length === 0 ? (
        <p data-testid="no-products" className="text-muted-foreground font-mono text-sm py-20 text-center">No compounds match your search.</p>
      ) : (
        <div data-testid="product-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {groupProducts(products).map((g, i) => (
            <ProductCard key={g.rep.id} product={g.rep} variants={g.variants} title={g.base} index={i} />
          ))}
        </div>
      )}
      </div>
  );
}
